import { AuditLogEntry, Booking, CustomerProfile, Field, OwnerProfile } from '../types';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const OWNERS_KEY = 'halisaha_owner_profiles';
const CUSTOMERS_KEY = 'halisaha_customer_profiles';
const FIELDS_KEY = 'halisaha_fields';
const EXTRA_BOOKINGS_KEY = 'halisaha_extra_bookings';
const AUDIT_LOGS_KEY = 'halisaha_audit_logs';

const TABLE_OWNERS = 'app_owner_profiles';
const TABLE_CUSTOMERS = 'app_customer_profiles';
const TABLE_FIELDS = 'app_fields';
const TABLE_BOOKINGS = 'app_bookings';
const TABLE_AUDIT_LOGS = 'app_audit_logs';

let hydrated = false;
let refreshTimer: number | undefined;
let refreshing = false;

export const DATA_CHANGED_EVENT = 'halisaha:data-changed';

export function notifyLocalDataChanged() {
  window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT));
}

function safeLocalRead<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeLocalWrite<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

async function upsertById<T extends { id: string }>(table: string, rows: T[]) {
  if (!isSupabaseConfigured || !supabase) return;
  const { error } = await supabase.from(table).upsert(rows as any, { onConflict: 'id' });
  if (error) {
    // Keep app flow uninterrupted when remote sync fails.
    console.warn(`[sync] upsert failed for ${table}:`, error.message);
  }
}

function ownerToRow(owner: OwnerProfile) {
  return {
    id: owner.id,
    full_name: owner.fullName,
    username: owner.username,
    email: owner.email ?? null,
    phone: owner.phone,
    password: owner.password,
    district: owner.district,
    status: owner.status,
    suspended: Boolean(owner.suspended),
    created_at: owner.createdAt,
  };
}

function rowToOwner(row: any): OwnerProfile {
  return {
    id: row.id,
    fullName: row.full_name,
    username: row.username,
    email: row.email ?? undefined,
    phone: row.phone,
    password: row.password,
    district: row.district,
    status: row.status,
    suspended: Boolean(row.suspended),
    createdAt: row.created_at,
  };
}

function customerToRow(user: CustomerProfile) {
  return {
    id: user.id,
    full_name: user.fullName,
    username: user.username,
    email: user.email ?? null,
    phone: user.phone ?? null,
    password: user.password ?? null,
    google_id: user.googleId ?? null,
    suspended: Boolean(user.suspended),
    created_at: user.createdAt,
  };
}

function rowToCustomer(row: any): CustomerProfile {
  return {
    id: row.id,
    fullName: row.full_name,
    username: row.username,
    email: row.email ?? undefined,
    phone: row.phone ?? '',
    password: row.password ?? undefined,
    googleId: row.google_id ?? undefined,
    suspended: Boolean(row.suspended),
    createdAt: row.created_at,
  };
}

function fieldToRow(field: Field) {
  return {
    id: field.id,
    owner_id: field.ownerId,
    name: field.name,
    district: field.district,
    lat: field.lat,
    lng: field.lng,
    maps_url: field.mapsUrl ?? null,
    location_input_type: field.locationInputType ?? null,
    address: field.address,
    price_per_hour: field.pricePerHour,
    rating: field.rating,
    review_count: field.reviewCount,
    size: field.size,
    size_options: field.sizeOptions ?? [field.size],
    images: field.images ?? [],
    amenities: field.amenities ?? {},
    turf_type: field.turfType,
    phone: field.phone,
    opening_hour: field.openingHour,
    closing_hour: field.closingHour,
    available_time_ranges: field.availableTimeRanges ?? null,
    available_time_ranges_by_day: field.availableTimeRangesByDay ?? null,
    slot_interval_minutes: field.slotIntervalMinutes ?? 60,
    subscription_plan: field.subscriptionPlan ?? null,
    subscription_enabled: Boolean(field.subscriptionEnabled),
    subscription_price: field.subscriptionPrice ?? null,
    subscription_rules: field.subscriptionRules ?? null,
    description: field.description ?? null,
    field_type: field.fieldType ?? null,
    is_reservation_paused: Boolean(field.isReservationPaused),
    reservation_paused_message: field.reservationPausedMessage ?? null,
    pricing_by_time_range: field.pricingByTimeRange ?? null,
    width: field.width ?? null,
    length: field.length ?? null,
    roof_type: field.roofType ?? null,
    has_scoreboard: field.hasScoreboard ?? null,
    has_goal_nets: field.hasGoalNets ?? null,
    max_capacity: field.maxCapacity ?? null,
    water_price: field.waterPrice ?? null,
    shoe_rental_price: field.shoeRentalPrice ?? null,
    payment_iban: field.paymentIban ?? null,
    payment_bank_name: field.paymentBankName ?? null,
    payment_account_name: field.paymentAccountName ?? null,
    payment_note: field.paymentNote ?? null,
    created_at: new Date().toISOString(),
  };
}

function rowToField(row: any): Field {
  return {
    id: row.id,
    ownerId: row.owner_id,
    name: row.name,
    district: row.district,
    lat: row.lat,
    lng: row.lng,
    mapsUrl: row.maps_url ?? undefined,
    locationInputType: row.location_input_type ?? undefined,
    address: row.address,
    pricePerHour: Number(row.price_per_hour || 0),
    rating: Number(row.rating || 0),
    reviewCount: Number(row.review_count || 0),
    size: row.size,
    sizeOptions: Array.isArray(row.size_options) ? row.size_options : [],
    images: Array.isArray(row.images) ? row.images : [],
    amenities: row.amenities || {},
    turfType: row.turf_type,
    phone: row.phone,
    openingHour: Number(row.opening_hour || 0),
    closingHour: Number(row.closing_hour || 24),
    availableTimeRanges: row.available_time_ranges ?? undefined,
    availableTimeRangesByDay: row.available_time_ranges_by_day ?? undefined,
    slotIntervalMinutes: 60,
    subscriptionPlan: row.subscription_plan ?? undefined,
    subscriptionEnabled: Boolean(row.subscription_enabled),
    subscriptionPrice: row.subscription_price ? Number(row.subscription_price) : 0,
    subscriptionRules: row.subscription_rules ?? undefined,
    description: row.description ?? undefined,
    fieldType: row.field_type ?? undefined,
    isReservationPaused: Boolean(row.is_reservation_paused),
    reservationPausedMessage: row.reservation_paused_message ?? undefined,
    pricingByTimeRange: row.pricing_by_time_range ?? undefined,
    width: row.width ?? undefined,
    length: row.length ?? undefined,
    roofType: row.roof_type ?? undefined,
    hasScoreboard: row.has_scoreboard ?? undefined,
    hasGoalNets: row.has_goal_nets ?? undefined,
    maxCapacity: row.max_capacity ?? undefined,
    waterPrice: row.water_price ?? undefined,
    shoeRentalPrice: row.shoe_rental_price ?? undefined,
    paymentIban: row.payment_iban ?? undefined,
    paymentBankName: row.payment_bank_name ?? undefined,
    paymentAccountName: row.payment_account_name ?? undefined,
    paymentNote: row.payment_note ?? undefined,
  };
}

function bookingToRow(booking: Booking) {
  return {
    id: booking.id,
    field_id: booking.fieldId,
    field_name: booking.fieldName,
    date: booking.date,
    hour: booking.hour,
    duration: booking.duration,
    team_name: booking.teamName,
    contact_name: booking.contactName,
    contact_phone: booking.contactPhone,
    total_price: booking.totalPrice,
    customer_id: booking.customerId ?? null,
    status: booking.status,
    payment_proof_url: booking.paymentProofUrl ?? null,
    payment_proof_note: booking.paymentProofNote ?? null,
    payment_proof_file_name: booking.paymentProofFileName ?? null,
    payment_proof_submitted_at: booking.paymentProofSubmittedAt ?? null,
    payment_proof_rejected_reason: booking.paymentProofRejectedReason ?? null,
    payment_reviewed_at: booking.paymentReviewedAt ?? null,
    created_at: booking.createdAt,
  };
}

function auditToRow(log: AuditLogEntry) {
  return {
    id: log.id,
    actor_role: log.actorRole,
    actor_id: log.actorId ?? null,
    action: log.action,
    target_type: log.targetType,
    target_id: log.targetId ?? null,
    meta: log.meta ?? null,
    created_at: log.createdAt,
  };
}

function rowToAudit(row: any): AuditLogEntry {
  return {
    id: row.id,
    actorRole: row.actor_role,
    actorId: row.actor_id ?? undefined,
    action: row.action,
    targetType: row.target_type,
    targetId: row.target_id ?? undefined,
    meta: row.meta ?? undefined,
    createdAt: row.created_at,
  };
}

function rowToBooking(row: any): Booking {
  return {
    id: row.id,
    fieldId: row.field_id,
    fieldName: row.field_name,
    date: row.date,
    hour: Number(row.hour || 0),
    duration: Number(row.duration || 1),
    teamName: row.team_name,
    contactName: row.contact_name,
    contactPhone: row.contact_phone,
    totalPrice: Number(row.total_price || 0),
    customerId: row.customer_id ?? undefined,
    status: row.status,
    paymentProofUrl: row.payment_proof_url ?? undefined,
    paymentProofNote: row.payment_proof_note ?? undefined,
    paymentProofFileName: row.payment_proof_file_name ?? undefined,
    paymentProofSubmittedAt: row.payment_proof_submitted_at ?? undefined,
    paymentProofRejectedReason: row.payment_proof_rejected_reason ?? undefined,
    paymentReviewedAt: row.payment_reviewed_at ?? undefined,
    createdAt: row.created_at,
  };
}

export async function hydrateLocalDataFromSupabase(): Promise<void> {
  await hydrateLocalDataFromSupabaseWithMode(false);
}

export async function refreshLocalDataFromSupabase(): Promise<void> {
  await hydrateLocalDataFromSupabaseWithMode(true);
}

async function hydrateLocalDataFromSupabaseWithMode(force: boolean): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;
  if (!force && hydrated) return;
  if (refreshing) return;
  refreshing = true;
  try {
    const [ownersRes, customersRes, fieldsRes, bookingsRes, auditRes] = await Promise.all([
      supabase.from(TABLE_OWNERS).select('*').order('created_at', { ascending: false }),
      supabase.from(TABLE_CUSTOMERS).select('*').order('created_at', { ascending: false }),
      supabase.from(TABLE_FIELDS).select('*').order('created_at', { ascending: false }),
      supabase.from(TABLE_BOOKINGS).select('*').order('created_at', { ascending: false }),
      supabase.from(TABLE_AUDIT_LOGS).select('*').order('created_at', { ascending: false }).limit(500),
    ]);

    if (!ownersRes.error && ownersRes.data) {
      safeLocalWrite<OwnerProfile[]>(OWNERS_KEY, ownersRes.data.map(rowToOwner));
    }
    if (!customersRes.error && customersRes.data) {
      safeLocalWrite<CustomerProfile[]>(CUSTOMERS_KEY, customersRes.data.map(rowToCustomer));
    }
    if (!fieldsRes.error && fieldsRes.data) {
      safeLocalWrite<Field[]>(FIELDS_KEY, fieldsRes.data.map(rowToField));
    }
    if (!bookingsRes.error && bookingsRes.data) {
      safeLocalWrite<Booking[]>(EXTRA_BOOKINGS_KEY, bookingsRes.data.map(rowToBooking));
    }
    if (!auditRes.error && auditRes.data) {
      safeLocalWrite<AuditLogEntry[]>(AUDIT_LOGS_KEY, auditRes.data.map(rowToAudit));
    }

    hydrated = true;
    notifyLocalDataChanged();
  } finally {
    refreshing = false;
  }
}

function scheduleRefresh(ms = 400) {
  if (refreshTimer) window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    void refreshLocalDataFromSupabase();
  }, ms);
}

export function subscribeToSupabaseDataChanges(): () => void {
  if (!isSupabaseConfigured || !supabase) return () => {};
  const client = supabase;

  const channel = client
    .channel('halisaha:public-data')
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLE_FIELDS }, () => {
      scheduleRefresh();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLE_BOOKINGS }, () => {
      scheduleRefresh();
    })
    .subscribe();

  return () => {
    void client.removeChannel(channel);
  };
}

export function syncOwnersToSupabase() {
  if (!isSupabaseConfigured || !supabase) return;
  const owners = safeLocalRead<OwnerProfile[]>(OWNERS_KEY, []);
  void upsertById(TABLE_OWNERS, owners.map(ownerToRow) as any);
}

export function syncCustomersToSupabase() {
  if (!isSupabaseConfigured || !supabase) return;
  const customers = safeLocalRead<CustomerProfile[]>(CUSTOMERS_KEY, []);
  void upsertById(TABLE_CUSTOMERS, customers.map(customerToRow) as any);
}

export function syncFieldsToSupabase() {
  if (!isSupabaseConfigured || !supabase) return;
  const allFields = safeLocalRead<Field[]>(FIELDS_KEY, []);
  void upsertById(TABLE_FIELDS, allFields.map(fieldToRow) as any);
}

export function syncBookingsToSupabase() {
  if (!isSupabaseConfigured || !supabase) return;
  const allBookings = safeLocalRead<Booking[]>(EXTRA_BOOKINGS_KEY, []);
  void upsertById(TABLE_BOOKINGS, allBookings.map(bookingToRow) as any);
}

export function syncAuditLogsToSupabase() {
  if (!isSupabaseConfigured || !supabase) return;
  const logs = safeLocalRead<AuditLogEntry[]>(AUDIT_LOGS_KEY, []);
  const recent = logs.slice(0, 1000);
  void upsertById(TABLE_AUDIT_LOGS, recent.map(auditToRow) as any);
}
